import { FormBuilder } from '@/components/formBuilder';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { KText } from '@/ui-library/components';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { Box, Button, Center, Flex, Icon } from '@chakra-ui/react';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { Download01 } from '@kula-ai/icons/outline/Download01';
import { File04 } from '@kula-ai/icons/outline/File04';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CenterSpinner } from '@/components/ats';
import {
  extractTokenArrays,
  getFilledValues,
  getUpdatedTokens,
} from '@/components/ats/OfferInitiation/offerFormHelpers';
import { useReplacementUploader } from '@/components/ats/OfferInitiation/PreviewOffer/useReplacementUploader';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { XClose } from '@kula-ai/icons/solid/XClose';
import {
  useGetAllOfferTemplatesQuery,
  useUploadReplacementMutation,
} from '../../api';
import { TokenValidation } from './TokenValidation';
import { DocumentPreview } from './DocumentPreview';
import type { TemplatesFormProps } from './TemplatesForm.interface';
import { useOfferStore } from '../../store';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';

const STATUS_CONFIG = {
  unverified: 'This template has unverified tokens',
  error: 'This template has invalid tokens',
  verified: 'All tokens verified',
  no_tokens: 'No tokens found',
};

export const NewTemplatesForm = ({
  templatesFormRef,
  initialValues,
  detailsFormRef,
  tabIndex,
  currentOffer,
  detailsFormConfig,
  templateProps,
  setTemplateProps,
  onTemplateSelect,
  initialDepartmentId,
  initialOfficeId,
}: TemplatesFormProps) => {
  const [regenerateKey, setRegenerateKey] = useState(0);
  const [isTemplateSelectionComplete, setIsTemplateSelectionComplete] =
    useState(true);
  const setHasValidTokens = useOfferStore((state) => state.setHasValidTokens);
  const hasInitializedRef = useRef(false);
  const offerPayload = useOfferStore((state) => state.offerPayload || null);
  const { currentJob } = useSelector(getAtsJobCreate);
  const jobEmploymentType = currentJob?.data?.basic_info?.employment_type;

  /* eslint-disable react-hooks/refs */
  const filterParams = useMemo(
    () => {
      if (tabIndex === 0) return null;
      return {
        filter_by: {
          department_ids:
            detailsFormRef?.current?.values?.ats_department_id ??
            initialDepartmentId,
          office_ids:
            detailsFormRef?.current?.values?.ats_office_id ?? initialOfficeId,
          ...(jobEmploymentType && { employment_type: [jobEmploymentType] }),
        },
        items: 100,
      };
    },
    [tabIndex, jobEmploymentType, initialDepartmentId, initialOfficeId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { data: templatesData, isFetching } = useGetAllOfferTemplatesQuery(
    filterParams ?? {},
    { enabled: filterParams !== null }
  );
  /* eslint-enable react-hooks/refs */

  const templates = useMemo(() => templatesData?.data || [], [templatesData]);
  const currentTemplate = templateProps?.template_value;

  // Extract template ID from initialValues to avoid re-renders from object reference changes
  const initialTemplateId = useMemo(
    () => initialValues?.templates?.ats_offer_template_id,
    [initialValues?.templates?.ats_offer_template_id]
  );
  const initialTemplateValue = useMemo(
    () => initialValues?.templates?.ats_offer_template,
    [initialValues?.templates?.ats_offer_template]
  );

  // Upload replacement mutation
  const uploadReplacement = useUploadReplacementMutation({
    onSuccess: async (data) => {
      if (data?.id) {
        setTemplateProps((prev) => ({
          ...prev,
          selected_template_id: data?.id,
          template_value: data,
        }));
        setRegenerateKey((prev) => prev + 1);
        setIsTemplateSelectionComplete(false);

        // Call the API to update the offer with the uploaded replacement template
        try {
          if (onTemplateSelect) {
            await onTemplateSelect(data?.id, data);
          }
          setIsTemplateSelectionComplete(true);
        } catch (error) {
          setIsTemplateSelectionComplete(true);
          throw error;
        }
      }
    },
  });

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadReplacement.mutate({
        id: currentOffer?.id,
        file,
      });
    }
  };

  const { ReplacementUploader } = useReplacementUploader({
    handleFileChange,
    replacedData: uploadReplacement.data,
    isReplacing: uploadReplacement.isPending,
    atsOffer: currentOffer,
  });

  // Set initial template from initialValues (only once)
  useEffect(() => {
    if (!initialTemplateId || hasInitializedRef.current) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: initialTemplateId,
      template_value: initialTemplateValue,
    }));
    hasInitializedRef.current = true;
  }, [initialTemplateId, initialTemplateValue, setTemplateProps]);

  // Calculate token validation
  const tokenValidation = useMemo(() => {
    if (!currentTemplate?.id || !currentTemplate?.tokens) {
      setHasValidTokens(true);
      return {
        validTokens: [],
        invalidTokens: [],
        filledTokensNotInTemplate: [],
        nonFilledValidTokens: [],
        hasValidationIssues: false,
      };
    }

    // eslint-disable-next-line react-hooks/refs
    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
    });

    const tokenArrays = extractTokenArrays({
      templateTokens: getUpdatedTokens(currentTemplate?.tokens),
      filledValues,
    });

    const hasInvalidTokens =
      tokenArrays.invalidTokens?.length > 0 ||
      tokenArrays.nonFilledValidTokens?.length > 0;

    setHasValidTokens(!hasInvalidTokens);

    return {
      ...tokenArrays,
      hasValidationIssues: hasInvalidTokens,
    };
  }, [
    currentTemplate,
    detailsFormConfig,
    detailsFormRef,
    setHasValidTokens,
    offerPayload,
  ]);

  // Form configuration
  const formConfig = useMemo(
    () => [
      {
        type: 'kSelect',
        name: 'ats_offer_template_id',
        label: 'Select offer letter template',
        placeholder: 'Select template',
        isRequired: true,
        options: [...templates]
          ?.sort((a, _b) => (a?.status === 'verified' ? -1 : 1))
          .map((temp) => ({
            label: temp?.name,
            value: temp?.id,
            desc:
              Object.keys(temp?.tokens ?? {})?.length === 0
                ? STATUS_CONFIG['no_tokens']
                : STATUS_CONFIG[temp?.status],
            isDisabled: temp?.status !== 'verified',
          })),
        isLoading: isFetching,
        descKey: 'desc',
        components: {
          Option: SelectWithDescriptionOption,
        },
        menuPortalTarget: document.body,
        customStyles: {
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        },
        customOnChange: async (value) => {
          const selectedTemplate = templates?.find(
            (i) => i?.id === value?.value
          );
          setTemplateProps((prev) => ({
            ...prev,
            selected_template_id: value?.value,
            template_value: selectedTemplate,
          }));
          setRegenerateKey((prev) => prev + 1);
          setIsTemplateSelectionComplete(false);

          // Call the API to update the offer with the selected template
          try {
            if (onTemplateSelect && value?.value && selectedTemplate) {
              await onTemplateSelect(value?.value, selectedTemplate);
            }
            setIsTemplateSelectionComplete(true);
          } catch (error) {
            setIsTemplateSelectionComplete(true);
            throw error;
          }
        },
      },
    ],
    [templates, isFetching, setTemplateProps, onTemplateSelect]
  );

  const handleRegenerate = () => {
    setRegenerateKey((prev) => prev + 1);
  };

  if (uploadReplacement.isPending) {
    return <CenterSpinner h="400px" />;
  }

  return (
    <Box h="100%" w="100%">
      {/* Template Selection Form */}
      {(currentTemplate?.kind === 'inline' || !currentTemplate?.id) && (
        <Box mt="-12px">
          <FormBuilder
            initialValues={initialValues?.templates}
            config={formConfig}
            formRef={templatesFormRef}
          />
        </Box>
      )}

      {/* Template Content */}
      {currentTemplate?.id && (
        <Box h="100%" w="100%">
          {/* Single Template Upload Display */}
          {currentTemplate?.kind === 'single' && (
            <Flex
              p="12px"
              border="1px solid"
              borderColor="border-color"
              borderRadius="sm"
              gap="12px"
              justifyContent="space-between"
              alignItems="center"
              mb="12px"
            >
              <Flex gap="12px" flex="1" alignItems="center" overflow="hidden">
                <Center
                  minH="44px"
                  minW="44px"
                  bgColor="secondary-50"
                  borderRadius="sm"
                >
                  <Icon as={File04} />
                </Center>
                <TruncatedTextWithTooltip
                  {...{ text: currentTemplate?.offer_document?.filename }}
                />
              </Flex>
              <Flex gap="12px" alignItems="center">
                <Center
                  p="6px 12px"
                  bgColor="pastel-2-light"
                  borderRadius="sm"
                  alignItems="center"
                  gap="6px"
                >
                  <Icon as={CheckCircle} color="success-300" />
                  <KText color="success-300">Uploaded</KText>
                </Center>
                <Icon
                  as={XClose}
                  cursor="pointer"
                  onClick={() => {
                    setTemplateProps((prev) => ({
                      ...prev,
                      selected_template_id: null,
                      template_value: {},
                    }));
                  }}
                />
              </Flex>
            </Flex>
          )}

          {/* Actions Row */}
          <Flex gap="12px" alignItems="center" justifyContent="space-between">
            <TokenValidation
              filledTokensNotInTemplate={
                tokenValidation.filledTokensNotInTemplate
              }
            />

            <Flex gap="12px">
              <Button
                leftIcon={<Icon as={Download01} />}
                cursor="pointer"
                onClick={() =>
                  downloadFileFromUrl({
                    url: currentTemplate?.offer_document?.url,
                    filename: currentTemplate?.offer_document?.filename,
                    useDirect: true,
                  })
                }
                variant="quaternary"
              >
                Download
              </Button>
              <ReplacementUploader isLoading={uploadReplacement.isPending} />
            </Flex>
          </Flex>

          {/* Token Validation Display */}
          {(tokenValidation?.nonFilledValidTokens?.length > 0 ||
            tokenValidation?.invalidTokens?.length > 0) && (
            <TokenValidation
              validTokens={tokenValidation.validTokens}
              invalidTokens={tokenValidation.invalidTokens}
              nonFilledValidTokens={tokenValidation.nonFilledValidTokens}
            />
          )}

          {/* Document Preview */}
          <DocumentPreview
            offerId={currentOffer?.id}
            templateId={templateProps?.selected_template_id}
            hasValidationIssues={tokenValidation.hasValidationIssues}
            onRegenerateTrigger={handleRegenerate}
            regenerateKey={regenerateKey}
            isTemplateSelectionComplete={isTemplateSelectionComplete}
          />
        </Box>
      )}
    </Box>
  );
};
